import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { useAddEventMutation } from "src/api/purchasing/events";
import { useGetPaymentMethods } from "src/api/generic";
import { useGetOCAccountsList } from "src/api/operation-cost/accounts";
import { useGetVendorsList } from "src/api/operation-cost/vendors";
import { useTranslation } from "react-i18next";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { convertObjectToFormData } from "src/utils";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
}

const schema = yup.object({
  mode: yup
    .string()
    .required("required")
    .oneOf(["add", "edit"] as const),
  vendor_id: yup.string().required("required"),
  direction: yup
    .string()
    .oneOf(["to_vendor", "from_vendor"])
    .required("required"),
  operation: yup.string().required("required"),
  payment_id: yup.string().when("operation", {
    is: (op: string) => ["refund", "advanced", "settlement"].includes(op),
    then: (v) => v.required("required"),
  }),
  account_id: yup.string().when("operation", {
    is: "expenses",
    then: (v) => v.required("required"),
  }),
  amount: yup.number().required("required"),
  date: yup.date().required("required"),
  note: yup.string(),
});

type FormInputs = yup.InferType<typeof schema>;

const VendorEventPopup = ({ open, handleClose, refetch }: Props) => {
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [files, setFiles] = useState<any>([]);
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  // apis
  const { mutate, isPending } = useAddEventMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: accountData } = useGetOCAccountsList({});
  const { data: vendorsData } = useGetVendorsList();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
    },
  });

  const direction = watch("direction");
  const operation = watch("operation");

  const getOperations = () => {
    if (direction === "to_vendor") {
      return [
        { value: "advanced", label: "Advance Payment" },
        { value: "settlement", label: "Settlement" },
        { value: "expenses", label: "Expenses" },
      ];
    }
    if (direction === "from_vendor") {
      return [
        { value: "refund", label: "Refund" },
        { value: "credit_note", label: "Credit Note" },
        { value: "other_revenue", label: "Other Revenue" },
        { value: "expenses", label: "Expenses" },
      ];
    }
    return [];
  };

  const shouldShowPayment = [
    "refund",
    "advanced",
    "settlement",
    "other_revenue",
    "credit_note",
  ].includes(operation);

  const onClose = () => {
    handleClose();
    reset({});
  };

  const onSubmit = (data: FormInputs) => {
    const payload = {
      type: "vendor",
      operation: data.direction,
      method: data.operation,
      amount: +data.amount,
      date: moment(data.date).format("YYYY-MM-DD"),
      vendor_id: data.vendor_id,
      ...(shouldShowPayment && { payment_id: data.payment_id }),
      ...(operation === "expenses" && { account_id: data.account_id }),

      ...(data.note && { note: data.note }),
    };

    const formData = convertObjectToFormData(payload);

    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              error?.data?.errors?.[0].value || generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  useEffect(() => {
    setValue("operation", "");
  }, [direction]);

  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", lg: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontSize="20px">{t("vendorEvent")}</Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            {/* Direction */}
            <FormControl size="small" error={!!errors.direction}>
              <Controller
                name="direction"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="to_vendor"
                      control={<Radio size="small" />}
                      label="To Vendor"
                    />
                    <FormControlLabel
                      value="from_vendor"
                      control={<Radio size="small" />}
                      label="From Vendor"
                    />
                  </RadioGroup>
                )}
              />
              <FormHelperText>{errors.direction?.message}</FormHelperText>
            </FormControl>

            {/* Select vendor */}
            <FormControl
              fullWidth
              size="small"
              error={!!errors.vendor_id}
              sx={{ mt: 1 }}
            >
              <InputLabel id="demo-simple-select-label">
                {t("vendor")}
              </InputLabel>
              <Controller
                name="vendor_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={t("vendor")}
                  >
                    {vendorsData?.data.map((ven: any) => {
                      return (
                        <MenuItem key={ven.id} value={ven.id}>
                          {ven.name}
                        </MenuItem>
                      );
                    })}
                    {vendorsData?.data?.length === 0 ? (
                      <Typography align="center" color={"textSecondary"}>
                        {generalT("noData")}
                      </Typography>
                    ) : null}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.vendor_id?.message}
              </FormHelperText>
            </FormControl>

            {/* Operation */}
            {direction && (
              <FormControl size="small" error={!!errors.operation}>
                <Controller
                  name="operation"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      {getOperations().map((op) => (
                        <FormControlLabel
                          key={op.value}
                          value={op.value}
                          control={<Radio size="small" />}
                          label={op.label}
                          disabled={op.value === "credit_note"}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormHelperText>{errors.operation?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Payment */}
            {shouldShowPayment && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.payment_id}
                sx={{ mt: 1 }}
              >
                <InputLabel>Payment</InputLabel>
                <Controller
                  name="payment_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Payment">
                      {paymentsData?.data?.map((p: any) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.payment_id?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Account (Expenses only) */}
            {operation === "expenses" && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.account_id}
                sx={{ mt: 1 }}
              >
                <InputLabel>Account</InputLabel>
                <Controller
                  name="account_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Account">
                      {accountData?.data?.map((acc: any) => (
                        <MenuItem key={acc.id} value={acc.id}>
                          {acc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.account_id?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Amount */}
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  label="Amount"
                  fullWidth
                  sx={{ mt: 1 }}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              )}
            />

            {/* Note */}
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t("notesOptional")}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 1 }}
                />
              )}
            />

            {/* Date */}
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format={"dd/MM/yyyy"}
                  label={`${t("transactionDateRepeat")}`}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: errors.date?.message,
                      inputProps: {
                        readOnly: true,
                        onKeyDownCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                        onPasteCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                      },
                    },
                  }}
                />
              )}
            />

            <FilePondUploader
              onUpload={(e) => setFiles(e)}
              maxFiles={3}
              maxFileSize={3}
              acceptedFileTypes={["image/*", "application/pdf"]}
            />
          </Stack>

          <Stack direction="row" spacing={2} mt={3}>
            <Button variant="outlined" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
            >
              Submit
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default VendorEventPopup;
